import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';

const ProjectDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showReferModal, setShowReferModal] = useState(false);

  // Check if the URL has the apply=true parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('apply') === 'true') {
      setShowApplyModal(true);
    }
  }, [location]);

  // Form states
  const [application, setApplication] = useState({
    coverLetter: '',
    availability: '',
    rate: '',
    resume: null
  });

  const [referral, setReferral] = useState({
    name: '',
    email: '',
    relationship: '',
    note: ''
  });

  useEffect(() => {
    // Mock data for a specific project
    const mockProjects = [
      {
        id: 1,
        title: 'M&A Due Diligence for Tech Acquisition',
        client: 'Confidential',
        industry: 'Technology',
        location: 'Remote',
        specialty: 'Management Consultant/Advisory',
        duration: '4-6 weeks',
        budget: '$15,000 - $25,000',
        description: 'Seeking experienced professionals to conduct financial and operational due diligence for a technology company acquisition. The target company specializes in SaaS solutions for the healthcare industry.',
        detailedDescription: 'The project involves conducting comprehensive due diligence on a healthcare SaaS company with approximately $10M in annual recurring revenue. The target company has been in operation for 7 years and has a team of 50 employees. The acquirer is a strategic buyer looking to expand their healthcare technology portfolio. The due diligence process will include financial analysis, operational assessment, technology evaluation, and regulatory compliance review.',
        responsibilities: [
          'Conduct financial due diligence including historical financial analysis and financial projections review',
          'Assess the quality of earnings and identify potential adjustments',
          'Evaluate the company\'s business model and revenue streams',
          'Review customer contracts and analyze customer concentration',
          'Identify potential synergies and integration challenges',
          'Prepare a comprehensive due diligence report with findings and recommendations'
        ],
        requirements: [
          'Experience in M&A due diligence',
          'Understanding of SaaS business models',
          'Knowledge of healthcare industry regulations',
          'Financial analysis and modeling skills'
        ],
        skills: ['Financial Analysis', 'Due Diligence', 'SaaS', 'Healthcare'],
        postedDate: '2025-04-15',
        deadline: '2025-04-30',
        status: 'Open',
        featured: true,
        contactPerson: 'John Smith',
        contactEmail: 'john.smith@example.com'
      },
      {
        id: 2,
        title: 'Valuation of Family-Owned Manufacturing Business',
        client: 'Smith Family Holdings',
        industry: 'Manufacturing',
        location: 'Chicago, IL',
        specialty: 'Valuation',
        duration: '2-3 weeks',
        budget: '$8,000 - $12,000',
        description: 'Family-owned manufacturing business seeking valuation services for potential sale. The business has been operating for over 30 years with stable revenue and profitability.',
        detailedDescription: 'Smith Family Holdings is a second-generation family-owned manufacturing business specializing in precision metal components for the automotive and aerospace industries. The company has been in operation for over 30 years and has maintained stable revenue and profitability. The owners are considering a potential sale and require a comprehensive business valuation to establish a fair market value. The company has annual revenue of approximately $15 million and EBITDA of $2.5 million.',
        responsibilities: [
          'Conduct a comprehensive business valuation using multiple methodologies',
          'Analyze historical financial performance and future projections',
          'Assess industry trends and competitive landscape',
          'Evaluate tangible and intangible assets',
          'Consider family ownership dynamics and transition planning',
          'Prepare a detailed valuation report with supporting analysis'
        ],
        requirements: [
          'Experience in business valuation',
          'Knowledge of manufacturing industry',
          'Understanding of family business dynamics',
          'Certification in business valuation preferred'
        ],
        skills: ['Business Valuation', 'Manufacturing', 'Family Business', 'Financial Modeling'],
        postedDate: '2025-04-14',
        deadline: '2025-04-28',
        status: 'Open',
        featured: true,
        contactPerson: 'Sarah Johnson',
        contactEmail: 'sarah.johnson@example.com'
      }
    ];

    // Find the project with the matching ID
    const foundProject = mockProjects.find(p => p.id === parseInt(id));

    // Simulate API call
    setTimeout(() => {
      setProject(foundProject);
      setLoading(false);
    }, 500);
  }, [id]);

  const handleApplyInputChange = (e) => {
    const { name, value } = e.target;
    setApplication({
      ...application,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    setApplication({
      ...application,
      resume: e.target.files[0]
    });
  };

  const handleReferInputChange = (e) => {
    const { name, value } = e.target;
    setReferral({
      ...referral,
      [name]: value
    });
  };

  const handleApplySubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send the application to an API
    console.log('Application submitted:', application);
    alert('Your application has been submitted successfully!');
    setShowApplyModal(false);
    // Reset form
    setApplication({
      coverLetter: '',
      availability: '',
      rate: '',
      resume: null
    });
  };

  const handleReferSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send the referral to an API
    console.log('Referral submitted:', referral);
    alert('Your referral has been submitted successfully!');
    setShowReferModal(false);
    // Reset form
    setReferral({
      name: '',
      email: '',
      relationship: '',
      note: ''
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Project Not Found</h1>
            <p className="mt-4 text-lg text-gray-600">The project you're looking for doesn't exist or has been removed.</p>
            <Link to="/projects" className="mt-6 inline-block bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200">
              Back to Project Board
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">{project.title}</h1>
            <p className="mt-2 text-lg">{project.client} • {project.industry} • {project.location}</p>
          </div>
        </div>
      </div>

      {/* Project Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Project Header */}
            <div className="bg-gray-50 p-6 border-b border-gray-200">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                <div>
                  <div className="flex items-center">
                    <h2 className="text-2xl font-bold text-gray-900">{project.title}</h2>
                    {project.featured && (
                      <span className="ml-2 inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mt-1">{project.client} • {project.specialty}</p>
                </div>
                <div className="mt-4 md:mt-0 flex space-x-4">
                  <button
                    onClick={() => setShowApplyModal(true)}
                    className="inline-flex items-center bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200"
                  >
                    Apply Now
                  </button>
                  <button
                    onClick={() => setShowReferModal(true)}
                    className="inline-flex items-center bg-white text-apn-blue border border-apn-blue py-2 px-6 rounded hover:bg-gray-50 transition duration-200"
                  >
                    Refer Someone
                  </button>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
                  <p className="text-gray-700">{project.location}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Duration</h3>
                  <p className="text-gray-700">{project.duration}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Budget</h3>
                  <p className="text-gray-700">{project.budget}</p>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Project Description</h3>
                <p className="text-gray-700 mb-4">{project.description}</p>
                <p className="text-gray-700">{project.detailedDescription}</p>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Responsibilities</h3>
                <ul className="list-disc pl-5 text-gray-700 space-y-2">
                  {project.responsibilities.map((responsibility, index) => (
                    <li key={index}>{responsibility}</li>
                  ))}
                </ul>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Requirements</h3>
                <ul className="list-disc pl-5 text-gray-700 space-y-2">
                  {project.requirements.map((requirement, index) => (
                    <li key={index}>{requirement}</li>
                  ))}
                </ul>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {project.skills.map((skill, index) => (
                    <span key={index} className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div>
                    <p className="text-gray-600">Posted on {new Date(project.postedDate).toLocaleDateString()}</p>
                    <p className="text-gray-600">Application Deadline: {new Date(project.deadline).toLocaleDateString()}</p>
                  </div>
                  <div className="mt-4 md:mt-0 flex space-x-4">
                    <button
                      onClick={() => setShowApplyModal(true)}
                      className="inline-flex items-center bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200"
                    >
                      Apply Now
                    </button>
                    <button
                      onClick={() => setShowReferModal(true)}
                      className="inline-flex items-center bg-white text-apn-blue border border-apn-blue py-2 px-6 rounded hover:bg-gray-50 transition duration-200"
                    >
                      Refer Someone
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Apply for Project</h2>
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                You are applying for: <span className="font-semibold">{project.title}</span>
              </p>
              <form onSubmit={handleApplySubmit}>
                <div className="mb-4">
                  <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-700 mb-1">
                    Cover Letter / Introduction
                  </label>
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    rows="5"
                    value={application.coverLetter}
                    onChange={handleApplyInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Introduce yourself and explain why you're a good fit for this project..."
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-1">
                    Availability
                  </label>
                  <input
                    type="text"
                    id="availability"
                    name="availability"
                    value={application.availability}
                    onChange={handleApplyInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="When can you start and what is your availability?"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="rate" className="block text-sm font-medium text-gray-700 mb-1">
                    Proposed Rate
                  </label>
                  <input
                    type="text"
                    id="rate"
                    name="rate"
                    value={application.rate}
                    onChange={handleApplyInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Your proposed rate or fee structure"
                  />
                </div>
                <div className="mb-6">
                  <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-1">
                    Resume / CV (PDF)
                  </label>
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf"
                    onChange={handleFileChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  />
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowApplyModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700"
                  >
                    Submit Application
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Refer Modal */}
      {showReferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Refer Someone for this Project</h2>
                <button
                  onClick={() => setShowReferModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                You are referring someone for: <span className="font-semibold">{project.title}</span>
              </p>
              <form onSubmit={handleReferSubmit}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Referral's Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={referral.name}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Full name of the person you're referring"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Referral's Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={referral.email}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Email address of the person you're referring"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Relationship
                  </label>
                  <select
                    id="relationship"
                    name="relationship"
                    value={referral.relationship}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  >
                    <option value="">Select your relationship</option>
                    <option value="Colleague">Colleague</option>
                    <option value="Classmate">Classmate</option>
                    <option value="Friend">Friend</option>
                    <option value="Professional Connection">Professional Connection</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
                    Referral Note
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    rows="4"
                    value={referral.note}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Why do you think this person would be a good fit for the project?"
                  ></textarea>
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowReferModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700"
                  >
                    Submit Referral
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Similar Projects */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Similar Projects</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="font-bold text-lg mb-2">Financial Due Diligence for Healthcare Acquisition</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">Conduct financial due diligence for a healthcare company acquisition with focus on revenue recognition and compliance.</p>
                <div className="flex justify-between text-xs text-gray-500 mb-4">
                  <span>Remote</span>
                  <span>3-4 weeks</span>
                </div>
                <Link to="/projects/3" className="text-apn-blue hover:underline text-sm">
                  View Details →
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="font-bold text-lg mb-2">SaaS Company Valuation</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">Perform valuation of a growing SaaS company for potential acquisition by a private equity firm.</p>
                <div className="flex justify-between text-xs text-gray-500 mb-4">
                  <span>New York, NY</span>
                  <span>2-3 weeks</span>
                </div>
                <Link to="/projects/4" className="text-apn-blue hover:underline text-sm">
                  View Details →
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="font-bold text-lg mb-2">Technology Sector Market Analysis</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">Conduct comprehensive market analysis of the enterprise software sector for a strategic planning initiative.</p>
                <div className="flex justify-between text-xs text-gray-500 mb-4">
                  <span>Remote</span>
                  <span>4-5 weeks</span>
                </div>
                <Link to="/projects/5" className="text-apn-blue hover:underline text-sm">
                  View Details →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Find More Projects</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Browse our project board to find more opportunities that match your skills and expertise.
          </p>
          <Link to="/projects" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            View All Projects
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
